import { MarkerClusterer } from "@googlemaps/markerclusterer"

const GOLD = '#C0A76E'
const DARK = '#394147'

const clusterSvg = (count: number, color: string) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
            <circle cx="22" cy="22" r="20" fill="${color}" stroke="#FEFDFC" stroke-width="2"/>
            <text x="22" y="22" text-anchor="middle" dominant-baseline="central" fill="#FEFDFC" font-family="Arial,sans-serif" font-size="13" font-weight="bold">${count}</text>
        </svg>`)}`

const clusterIcon = (count: number, color: string) => ({
    url: clusterSvg(count, color),
    scaledSize: new google.maps.Size(44, 44),
    anchor: new google.maps.Point(22, 22),
})

const clusterRenderer = {
    render: ({ count, position }: { count: number, position: google.maps.LatLng }) =>
        new google.maps.Marker({
            position,
            icon: clusterIcon(count, GOLD),
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        })
}

let map: google.maps.Map
let clusterer: MarkerClusterer | null = null
export async function initMap(mapEl: HTMLElement) {
    if (!mapEl || mapEl.hasAttribute('data-initialized')) return

    mapEl.setAttribute('data-initialized', '')
    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary,
        { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary;

    const type = mapEl.dataset.map ?? "single",
        themeRoot = mapEl.dataset.themeUri,
        mapOptions: google.maps.MapOptions = {
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            styles: mapStyle
        }

    if (type === 'single') {
        let location: google.maps.LatLng = null;

        const address = mapEl.dataset.address ?? null
        if (address) {
            const geocoder = new Geocoder();

            await geocoder.geocode({ address }, (results, status) => {
                if (status !== "OK") return
                location = results[0]?.geometry.location ?? null
            })
        } else {
            const lat = mapEl.dataset.lat ?? null,
                lng = mapEl.dataset.lng ?? null

            if (lat && lng) {
                location = new google.maps.LatLng(parseFloat(lat), parseFloat(lng))
            }
        }

        if (!location) return;

        mapOptions.disableDefaultUI = false
        mapOptions.center = location
        mapOptions.zoom = parseInt(mapEl.dataset.zoom ?? '13')
    } else if (type === 'salons') {
        mapOptions.center = { lat: 50.0596405, lng: 14.3832248 }
        mapOptions.zoom = 11
        mapOptions.isFractionalZoomEnabled = true
    }

    let markers = [];
    map = new Map(mapEl, mapOptions);

    if (type === 'single') {
        new google.maps.Marker({
            position: mapOptions.center,
            clickable: false,
            icon: {
                url: `${themeRoot}/resources/icons/pin.svg`,
                scaledSize: new google.maps.Size(35, 35),
            },
            map: map
        });
    } else if (type === 'salons') {
        const bounds = new google.maps.LatLngBounds(),
            isArchiveMap = document.body.classList.contains('archive'),
            salons = typeof salonsData !== 'undefined' ? salonsData
                : isArchiveMap ? Livewire.first().mapPins : []

        salons.forEach(salon => {
            if (!salon.lat.length || !salon.lng.length) return

            const marker = createSalonMarker(salon, isArchiveMap, true)
            markers.push(marker)
            bounds.extend(marker.getPosition())
        })

        clusterer = new MarkerClusterer({
            map, markers, renderer: clusterRenderer,
            onClusterClick: (_, cluster, map) => {
                fitBoundsWithMaxZoom(map, cluster.bounds)
            }
        })

        const urlParams = new URLSearchParams(window.location.search)
        const souradnice = urlParams.get('souradnice')

        if (souradnice && souradnice.split(',').length === 2) {
            const [lat, lng] = souradnice.split(',').map(Number)
            map.setCenter({ lat, lng })
            map.setZoom(12)
        } else if (salons.length > 1) {
            fitBoundsWithMaxZoom(map, bounds)
        } else if (salons.length) {
            map.setCenter(getSalonLocation(salons[0]))
            map.setZoom(14)
        }
    }

    map.addListener("tilesloaded", () => mapEl.classList.add('is-loaded'))

    const insertBefore = document.head.insertBefore
    document.head.insertBefore = function(newElement: Node, referenceElement: Node) {
        const newLinkElement = newElement as HTMLLinkElement;
        if (newLinkElement.href && newLinkElement.href.indexOf("//fonts.googleapis.com/") > -1) return null;

        insertBefore.call(document.head, newElement, referenceElement);
    }

    if (!document.body.classList.contains('archive')) return;

    document.querySelector('[data-location-find-me]')?.addEventListener("click", () => {
        navigator.geolocation.getCurrentPosition(async (position) => {
            await new Geocoder().geocode({
                'location': new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
                'region': "cz"
            }).then((response) => {
                const locality = response.results?.find(response =>
                    response.types.includes('locality'))?.formatted_address ?? ''

                window.dispatchEvent(new CustomEvent("setLocationSearchQuery", { detail: { locality } }))
                Livewire.first().$call('searchByLocation', [position.coords.latitude, position.coords.longitude])

                localStorage.setItem(`${position.coords.latitude},${position.coords.longitude}`, locality)
            }).catch((error) => {
                console.log(error);
            });
        });
    });

    const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

    let predictions: google.maps.places.PlacePrediction[] = [];
    let requestBody = {
        input: "",
        includedRegionCodes: ["cz"],
        includedPrimaryTypes: [
            "geocode"
        ],
        language: "cs-CZ",
        region: "cz"
    }

    //@ts-ignore
    requestBody.sessionToken = new AutocompleteSessionToken();

    window.addEventListener("getLocationSuggestions", async (e: CustomEvent) => {
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            ...requestBody,
            input: e.detail.search ?? ''
        });

        predictions = suggestions.map(suggestion => suggestion.placePrediction)
        window.dispatchEvent(new CustomEvent("handleLocationSuggestions", { detail: {
            suggestions: predictions.map(prediction => prediction!.text.toString())
        }}))
    })

    window.addEventListener("selectLocationSuggestions", async (e: CustomEvent) => {
        const place = predictions[e.detail.placePrediction]?.toPlace()
        await place.fetchFields({
            fields: ['location']
        })

        Livewire.first().$call('searchByLocation', [place.location.lat(), place.location.lng()])
        localStorage.setItem(`${place.location.lat()},${place.location.lng()}`, predictions[e.detail.placePrediction]!.text.toString())

        //@ts-ignore
        requestBody.sessionToken = new AutocompleteSessionToken();
    })

    const dispatchMapChange = () => {
        if(!isMapBeyondFirstRender(mapEl)) return

        const mapBounds = map.getBounds(),
            bounds = [
                mapBounds.getSouthWest().lat(),
                mapBounds.getSouthWest().lng(),
                mapBounds.getNorthEast().lat(),
                mapBounds.getNorthEast().lng()
            ]

        Livewire.first().$call('searchByMap', bounds)
    }

    let dragEvent, zoomEvent;

    const addUserMapListeners = () => {
        dragEvent = map.addListener("dragend", dispatchMapChange)
        zoomEvent = map.addListener("zoom_changed", dispatchMapChange)
    }

    const removeUserMapListeners = () => {
        google.maps.event.removeListener(dragEvent);
        google.maps.event.removeListener(zoomEvent);
    }

    addUserMapListeners();

    Livewire.on('change-map', (params: { type: string, coordinates?: string }) => {
        clusterer?.clearMarkers()
        markers = []

        const bounds = new google.maps.LatLngBounds(),
            salons = Livewire.first().mapPins as mapPinData[]

        salons.forEach(salon => {
            if (!salon.lat.length || !salon.lng.length) return

            const marker = createSalonMarker(salon, true, true)

            markers.push(marker)
            bounds.extend(marker.getPosition())
        })

        clusterer?.addMarkers(markers)
        
        if (params.type === 'mapy') return;
        removeUserMapListeners();

        if (params.type === 'lokality' && params.coordinates) {
            const [lat, lng] = params.coordinates.split(',').map(Number)
            map.panTo({ lat, lng })
            map.setZoom(12)
        } else if (salons.length > 1) {
            fitBoundsWithMaxZoom(map, bounds)
        } else if (salons.length) {
            map.panTo(getSalonLocation(salons[0]))
            map.setZoom(14)
        }

        setTimeout(() => {
            addUserMapListeners();
        }, 400)
    })

    document.querySelector('.SalonArchiveGrid').addEventListener("mouseover", (e: MouseEvent) => {
        const card = e.target;
        if (card instanceof HTMLElement && card.classList.contains('Salon')) {
            const themeUri = map.getDiv().dataset.themeUri
            const targetMarker = markers.find(marker => marker.salonID === parseInt(card.getAttribute('wire:key')))

            if (!targetMarker) return

            if (targetMarker.getMap()) {
                targetMarker.setIcon({
                    url: `${themeUri}/resources/icons/pin-dark.svg`,
                    scaledSize: new google.maps.Size(32, 32),
                })
            } else if (clusterer) {
                const cluster = clusterer.clusters.find(c => c.count > 1 && c.markers?.includes(targetMarker))
                if (cluster?.marker) {
                    (cluster.marker as google.maps.Marker).setIcon(clusterIcon(cluster.count, DARK))
                }
            }

            card.addEventListener("mouseleave", () => {
                markers.forEach(marker => marker.setIcon({
                    url: `${themeUri}/resources/icons/pin.svg`,
                    scaledSize: new google.maps.Size(32, 32),
                }))

                clusterer?.clusters.forEach(c => {
                    if (c.marker && c.count > 1) {
                        (c.marker as google.maps.Marker).setIcon(clusterIcon(c.count, GOLD))
                    }
                })
            }, { once: true });
        }
    })
}

const MD_BREAKPOINT = 1024
const SALONS_MAX_ZOOM = 14
const getSalonsPadding = () => window.innerWidth >= MD_BREAKPOINT ? 120 : 40

const fitBoundsWithMaxZoom = (map: google.maps.Map, bounds: google.maps.LatLngBounds, padding: number = getSalonsPadding(), maxZoom: number = SALONS_MAX_ZOOM) => {
    map.fitBounds(bounds, padding)
    const listener = map.addListener('idle', () => {
        google.maps.event.removeListener(listener)
        if (map.getZoom() > maxZoom) {
            map.setZoom(maxZoom)
        }
    })
}

const getSalonLocation = (salon: mapPinData) => {
    return { lat: parseFloat(salon.lat), lng: parseFloat(salon.lng) }
}

const createSalonMarker = (salon: mapPinData, isArchiveMap: boolean = false, useClusterer: boolean = false) => {
    const marker =  new google.maps.Marker({
        position: getSalonLocation(salon),
        clickable: isArchiveMap,
        icon: {
            url: `${map.getDiv().dataset.themeUri}/resources/icons/pin.svg`,
            scaledSize: new google.maps.Size(32, 32),
        },
        map: useClusterer ? null : map
    });

    // @ts-ignore
    marker.salonID = salon.ID;
    isArchiveMap && marker.addListener("click", () => { window.location.href = salon.permalink });

    return marker;
}

const isMapBeyondFirstRender = (mapEl: HTMLElement) => {
    return mapEl.classList.contains('is-loaded')
}

const mapStyle = [
    {
        featureType: "landscape",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }],
    },
    {
        stylers: [{ hue: "#00aaff" }, { saturation: -100 }, { gamma: 2.15 }, { lightness: 12 }],
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ visibility: "on" }, { lightness: 24 }],
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ lightness: 57 }],
    },
]